import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { AgentPlan, Agent } from "../api/client.js";
import {
  generatePlan,
  approvePlan,
  rejectPlan,
  executePlan,
  listAgents,
} from "../api/client.js";

export function PlannerPage() {
  const navigate = useNavigate();
  const [goal, setGoal] = useState("");
  const [plan, setPlan] = useState<AgentPlan | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!goal.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const [planResult, agentResult] = await Promise.all([
        generatePlan(goal),
        listAgents(),
      ]);
      setPlan(planResult);
      setAgents(agentResult.agents);
      if (agentResult.agents.length > 0) {
        setSelectedAgent(agentResult.agents[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate plan");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!plan) return;
    try {
      const updated = await approvePlan(plan.id);
      setPlan(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to approve plan");
    }
  };

  const handleReject = async () => {
    if (!plan) return;
    const updated = await rejectPlan(plan.id);
    setPlan(updated);
  };

  const handleExecute = async () => {
    if (!plan || !selectedAgent) return;
    setLoading(true);
    try {
      const result = await executePlan(plan.id, selectedAgent);
      navigate(`/workflows/${result.workflowId}/edit`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Execution failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-ink-950 mb-1">Planner</h1>
      <p className="text-sm text-ink-500 mb-6">
        Describe a goal and get an AI-generated execution plan
      </p>

      {/* Goal input */}
      <div className="mb-6">
        <textarea
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          placeholder="Describe what you want to accomplish..."
          rows={3}
          className="w-full rounded-xl border border-ink-200 p-4 text-sm focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500 resize-none"
        />
        <button
          onClick={handleGenerate}
          disabled={loading || !goal.trim()}
          className="mt-3 rounded-lg bg-accent-600 px-6 py-2 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50"
        >
          {loading ? "Generating..." : "Generate Plan"}
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</div>
      )}

      {/* Plan display */}
      {plan && (
        <div className="rounded-xl border border-ink-100 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-ink-950">Execution Plan</h2>
              <p className="text-sm text-ink-400">
                {plan.steps.length} steps, ~{Math.round(plan.estimatedDurationMs / 1000)}s estimated
              </p>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
              plan.status === "draft" ? "bg-amber-100 text-amber-700"
              : plan.status === "approved" ? "bg-green-100 text-green-700"
              : plan.status === "rejected" ? "bg-red-100 text-red-700"
              : plan.status === "executing" ? "bg-blue-100 text-blue-700"
              : "bg-ink-100 text-ink-600"
            }`}>
              {plan.status}
            </span>
          </div>

          <div className="mb-4">
            <span className="text-xs font-medium text-ink-400">Goal</span>
            <p className="text-sm text-ink-700">{plan.goal}</p>
          </div>

          <div className="mb-4">
            <span className="text-xs font-medium text-ink-400 mb-2 block">Tools Used</span>
            <div className="flex gap-1.5">
              {plan.toolsUsed.map((tool) => (
                <span key={tool} className="rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                  {tool}
                </span>
              ))}
            </div>
          </div>

          {/* Steps */}
          <div className="space-y-3 mb-6">
            {plan.steps.map((step, i) => (
              <div key={step.id} className="rounded-lg border border-ink-50 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-100 text-xs font-bold text-accent-700">
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-ink-800">{step.description}</h3>
                    <div className="mt-1 flex items-center gap-3 text-xs text-ink-400">
                      <span className="font-mono">{step.toolName} v{step.toolVersion}</span>
                      {step.dependencies.length > 0 && (
                        <span>depends on: {step.dependencies.join(", ")}</span>
                      )}
                      {step.safetyBudget.maxDurationMs && (
                        <span>max {step.safetyBudget.maxDurationMs / 1000}s</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Actions */}
          {plan.status === "draft" && (
            <div className="flex gap-3">
              <button
                onClick={handleApprove}
                className="rounded-lg bg-green-600 px-6 py-2 text-sm font-medium text-white hover:bg-green-700"
              >
                Approve Plan
              </button>
              <button
                onClick={handleReject}
                className="rounded-lg border border-red-200 px-6 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
              >
                Reject
              </button>
            </div>
          )}

          {plan.status === "approved" && (
            <div className="flex items-center gap-3">
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                className="rounded-lg border border-ink-200 px-3 py-2 text-sm"
              >
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              <button
                onClick={handleExecute}
                disabled={loading || !selectedAgent}
                className="rounded-lg bg-accent-600 px-6 py-2 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50"
              >
                {loading ? "Executing..." : "Execute Plan"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
